import { useCallback, useEffect, useRef, useState } from "react";

import useAuth from "./useAuth";
import useRetryFetch from "./useRetryFetch";
import {
  getVapidPublicKey,
  removePushSubscription,
  savePushSubscription,
} from "../api/push";

export type PushStatus =
  | "unsupported" // browser can't do web push (incl. iOS Safari outside the installed PWA)
  | "denied" // user blocked notifications at the browser level
  | "off" // available but not subscribed on this device
  | "on"; // subscribed on this device

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from(Array.from(raw).map((c) => c.charCodeAt(0)));
};

const isSupported = () =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/**
 * One device's push state + the enable/disable actions. On every launch an
 * existing subscription is re-synced to the server, which also heals
 * subscriptions rotated by the push service (see the service worker's
 * pushsubscriptionchange handler).
 */
export function usePushNotifications() {
  const { auth } = useAuth();
  const retryFetch = useRetryFetch();
  const [status, setStatus] = useState<PushStatus>(
    isSupported()
      ? Notification.permission === "denied"
        ? "denied"
        : "off"
      : "unsupported"
  );
  const [busy, setBusy] = useState(false);
  const synced = useRef(false);

  // Detect an existing subscription and keep the server copy fresh
  useEffect(() => {
    if (!isSupported() || !auth?.accessToken || synced.current) return;
    synced.current = true;
    (async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription) {
          setStatus("on");
          savePushSubscription(retryFetch, subscription).catch(() => {});
        }
      } catch {
        /* stay at the permission-derived status */
      }
    })();
  }, [auth?.accessToken, retryFetch]);

  /** Must be called from a user gesture (permission prompt). */
  const enable = useCallback(async (): Promise<boolean> => {
    if (!isSupported() || busy) return false;
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus(permission === "denied" ? "denied" : "off");
        return false;
      }

      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return false;

      const publicKey = await getVapidPublicKey();
      if (!publicKey) return false;

      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));

      const saved = await savePushSubscription(retryFetch, subscription);
      if (saved) setStatus("on");
      return saved;
    } catch {
      return false;
    } finally {
      setBusy(false);
    }
  }, [busy, retryFetch]);

  const disable = useCallback(async (): Promise<void> => {
    if (!isSupported() || busy) return;
    setBusy(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        removePushSubscription(retryFetch, subscription.endpoint).catch(() => {});
        await subscription.unsubscribe();
      }
      setStatus("off");
    } finally {
      setBusy(false);
    }
  }, [busy, retryFetch]);

  return { status, busy, enable, disable };
}
